import OpenPlaysLink from "@/components/OpenPlaysLink";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Zap, Mail, Phone, MapPin, Facebook, Instagram, HelpCircle, Calendar, Compass } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import VenueRentalNotice from "@/components/VenueRentalNotice";
import AnnouncementsCarousel from "@/components/AnnouncementsCarousel";

// FAQ list lives outside the component because it never changes between
// renders. Each entry's `body` is a render fn so we can embed links.
const FAQ_ITEMS = [
  {
    q: "Where are you located?",
    body: () => (
      <>
        We are located along Centennial Road, Tabon 1, Kawit, Cavite between
        Kenny Rogers-Centennial and Shakey's Pizza Parlor-Centennial, across
        Phoenix Gas Station-Kawit.
        <br />
        <br />
        Use this Waze/Google pin (
        <span className="font-semibold text-[#8cc63f]">ASSENDA KAWIT</span>):{" "}
        <a
          href="https://maps.app.goo.gl/DaeCFPcwCp4xvnci6"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="faq-map-link"
          className="text-[#8cc63f] underline hover:text-[#9ed15c] break-all"
        >
          https://maps.app.goo.gl/DaeCFPcwCp4xvnci6
        </a>
      </>
    ),
  },
  {
    q: "What are your operating hours?",
    body: () => <>We are open daily from 7:00 AM to 2:00 AM.</>,
  },
  {
    q: "Can we rent the courts for private events or tournaments?",
    body: () => (
      <>
        Private bookings and tournaments are welcome. You may email us or send
        an inquiry on our social media accounts to check availability and
        rates.
      </>
    ),
  },
  {
    q: "What is your cancellation and refund policy?",
    body: () => (
      <>
        Cancellation must be made at least 12 hours in advance. Rescheduling is
        allowed but payments are non-refundable.
        <br />
        Late cancellations or no-shows are non-refundable.
      </>
    ),
  },
  {
    q: "Do you have restrooms, showers, and changing areas?",
    body: () => (
      <>
        Yes, we have restrooms, showers, and changing rooms available for all
        guests.
      </>
    ),
  },
  {
    q: "Are kids allowed?",
    body: () => (
      <>
        Yes! Kids are allowed as long as they are accompanied by an adult and
        follow all court safety rules.
      </>
    ),
  },
  {
    q: "Can we bring outside food and drinks?",
    body: () => (
      <>
        <span className="block">
          Individually packed meals are allowed. Bringing of{" "}
          <span className="font-semibold text-white">party trays and group meals</span>{" "}
          have a{" "}
          <span className="font-semibold text-white">corkage fee of ₱100 per head</span>.
        </span>
        <span className="block mt-2">
          Outside alcoholic beverages are prohibited — we have available liquors
          and cocktails at{" "}
          <span className="font-semibold text-white">Assenda Cafe</span>.
        </span>
      </>
    ),
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);
  // Send the customer directly to the All Courts availability view.
  const bookNow = () => navigate("/availability");

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-3">
          <h1 className="flex items-center">
            <img
              src="/assenda-logo.png"
              alt="Assenda"
              className="h-8 sm:h-10 w-auto object-contain"
              data-testid="brand-logo"
            />
          </h1>
          <div className="flex items-center gap-2 sm:gap-3">
            <OpenPlaysLink />
            <button
              type="button"
              data-testid="header-events-link"
              onClick={() => navigate("/events")}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold text-white/80 hover:text-[#8cc63f] transition-colors"
              title="Events &amp; Coaching Sessions"
            >
              <Calendar size={14} />
              Events
            </button>
            <button
              data-testid="header-contact-btn"
              onClick={() => setContactOpen(true)}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-[#8cc63f] border border-[#8cc63f]/40 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
            >
              Contact Us
            </button>
            <button
              data-testid="header-book-now-btn"
              onClick={bookNow}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold bg-[#8cc63f] text-black rounded-sm hover:bg-[#9ed15c] transition-colors inline-flex items-center gap-1.5"
            >
              <Zap size={14} />
              Book a Court
            </button>
            <button
              data-testid="admin-login-link"
              onClick={() => navigate("/admin/login")}
              className="text-xs font-semibold text-[#a1a1aa] hover:text-[#8cc63f] transition-colors px-1 sm:ml-1"
            >
              Admin
            </button>
          </div>
        </div>
      </header>

      {/* Contact dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-[#121212] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tighter">
              Contact <span className="text-[#8cc63f]">Assenda</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 text-sm">
            <a
              href="mailto:assendaevents@gmail.com"
              data-testid="contact-email"
              className="flex items-center gap-3 p-3 rounded-sm border border-white/10 hover:border-[#8cc63f] transition-colors"
            >
              <Mail size={18} className="text-[#8cc63f]" />
              <div>
                <p className="label-text">Email</p>
                <p className="font-semibold">assendaevents@gmail.com</p>
              </div>
            </a>
            <a
              href="tel:+639989892051"
              data-testid="contact-phone"
              className="flex items-center gap-3 p-3 rounded-sm border border-white/10 hover:border-[#8cc63f] transition-colors"
            >
              <Phone size={18} className="text-[#8cc63f]" />
              <div>
                <p className="label-text">Phone</p>
                <p className="font-semibold">+63 998 989 2051</p>
              </div>
            </a>
            <a
              href="https://maps.app.goo.gl/DaeCFPcwCp4xvnci6"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-location"
              className="flex items-center gap-3 p-3 rounded-sm border border-white/10 hover:border-[#8cc63f] transition-colors"
            >
              <MapPin size={18} className="text-[#8cc63f]" />
              <div>
                <p className="label-text">Location</p>
                <p className="font-semibold">Centennial Road, Tabon 1, Kawit, Cavite, Philippines</p>
                <p className="text-xs text-[#a1a1aa] mt-0.5">Kindly use this pin on Google Maps or Waze: <span className="text-[#8cc63f]">📍 Assenda Kawit</span></p>
              </div>
            </a>
            <div className="flex gap-3 pt-2">
              <a
                href="https://web.facebook.com/profile.php?id=61577817171416"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-facebook"
                className="flex-1 inline-flex items-center justify-center gap-2 py-2 border border-white/10 rounded-sm hover:border-[#8cc63f] hover:text-[#8cc63f] transition-colors"
              >
                <Facebook size={16} /> Facebook
              </a>
              <a
                href="https://www.instagram.com/assendaph/?hl=en"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram"
                className="flex-1 inline-flex items-center justify-center gap-2 py-2 border border-white/10 rounded-sm hover:border-[#8cc63f] hover:text-[#8cc63f] transition-colors"
              >
                <Instagram size={16} /> Instagram
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <section className="hero-section">
        <img
          src="https://customer-assets.emergentagent.com/job_court-reserve-54/artifacts/28iopn7z_myarchitectai_mrkk5lkbdl_sd.png"
          alt="Assenda Pickleball Facility"
          className="hero-bg"
        />
        <div className="hero-overlay"></div>
        <div className="hero-content text-center px-6 max-w-4xl w-full">
          {/* Live announcements sit ABOVE the wordmark so tournaments and
              upcoming events are the first thing visitors see. */}
          <AnnouncementsCarousel />
          <img
            src="https://customer-assets.emergentagent.com/job_court-reserve-54/artifacts/0puilx47_ASSENDA-2%20copy_Green%20%281%29_homepage%20logo.png"
            alt="Assenda"
            className="w-full max-w-[340px] sm:max-w-md lg:max-w-xl mx-auto mt-6 sm:mt-8 mb-0 drop-shadow-2xl"
            data-testid="assenda-wordmark"
          />
          <p className="text-xs sm:text-base lg:text-lg italic text-white font-semibold tracking-wide -mt-1 sm:mt-0 mb-5 sm:mb-8">
            Ascending play to <span className="text-[#8cc63f]">greater heights</span>
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="book-now-button"
              onClick={bookNow}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Zap size={20} />
              Book a Court
            </button>
            <a
              href="/op"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-open-plays-link"
              title="Open Plays on Sports360"
              aria-label="Open Plays on Sports360"
              className="inline-flex items-center gap-2 px-5 py-3 text-sm sm:text-base font-bold text-[#8cc63f] border border-[#8cc63f]/50 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_court-reserve-54/artifacts/3apmhov4_sports360-logo-login-Cd80Vo2N.png"
                alt=""
                aria-hidden="true"
                className="w-5 h-5 rounded-sm object-contain"
              />
              <span className="whitespace-nowrap">Open Plays</span>
            </a>
          </div>
          {/* Secondary CTA — smaller, sits below the two primary buttons so
              it doesn't compete visually but stays scannable in the hero. */}
          <div className="mt-4 flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-3">
            <button
              type="button"
              data-testid="hero-events-coaching-btn"
              onClick={() => navigate("/events")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-[0.14em] text-white/80 border border-white/20 rounded-sm hover:border-[#8cc63f] hover:text-[#8cc63f] transition-colors"
            >
              <Calendar size={13} />
              Events &amp; Coaching Sessions
            </button>
            <a
              href="https://sites.google.com/view/assendadirectory/home"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-directory-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-[0.14em] text-white/80 border border-white/20 rounded-sm hover:border-[#8cc63f] hover:text-[#8cc63f] transition-colors"
            >
              <Compass size={13} />
              Explore Amenities, Dining &amp; Merchandise
            </a>
          </div>
        </div>
      </section>

      {/* Full Pricelist */}
      <section className="section-padding bg-[#0a0a0a] border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          {/* Exclusive 4-court venue rental CTA — kept as the sole tile in
              this section after the pricelist cards were removed. */}
          <VenueRentalNotice />
        </div>
      </section>

      {/* About Us — cream tonal break that matches the brand asset.
          Uses the dark-green logotype + italic body the venue provided. */}
      <section
        className="section-padding bg-[#fbf6cb] text-[#1f4d2f] border-t border-white/10"
        data-testid="about-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <img
            src="/assenda-logo-dark.png"
            alt="Assenda"
            data-testid="about-logo"
            className="h-12 sm:h-14 w-auto mx-auto mb-6"
            onError={(e) => {
              // Fallback: if the dark variant isn't available, render the
              // wordmark as styled text so the section never breaks.
              e.currentTarget.style.display = "none";
              const sib = e.currentTarget.nextElementSibling;
              if (sib) sib.style.display = "block";
            }}
          />
          <h2
            className="hidden text-3xl sm:text-4xl font-black tracking-tighter mb-6"
            aria-hidden="true"
          >
            ASSENDA
          </h2>
          <p className="label-text text-[#1f4d2f] mb-8 tracking-[0.25em]">About Us</p>
          <p className="italic font-semibold text-base sm:text-lg leading-relaxed">
            By players for players,{" "}
            <span className="font-black not-italic">ASSENDA</span> is a
            pickleball and lifestyle space in Kawit, Cavite. Offering a unique
            playing experience through its outdoor dome court, alongside various
            offerings such as a cafe, food stalls, car detailing, and a
            celebration venue, Assenda becomes your one-stop hub for building
            your community stronger and better, on and off the court, with every
            visit.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-[#0a0a0a] border-t border-white/10" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="label-text mb-3 inline-flex items-center gap-2 justify-center">
              <HelpCircle size={14} className="text-[#8cc63f]" />
              Need to know
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tighter font-black mb-4">
              Frequently Asked <span className="text-[#8cc63f]">Questions</span>
            </h2>
            <p className="text-sm text-[#a1a1aa]">
              Quick answers about location, bookings, and what to expect when you visit.
            </p>
          </div>
          <Accordion
            type="single"
            collapsible
            className="rounded-md border border-white/10 bg-[#121212] divide-y divide-white/10"
          >
            {FAQ_ITEMS.map((item, idx) => (
              <AccordionItem
                key={item.q}
                value={`faq-${idx}`}
                className="border-b-0 px-4 sm:px-6"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${idx}`}
                  className="text-left text-sm sm:text-base font-semibold text-white hover:no-underline hover:text-[#8cc63f] py-4"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${idx}`}
                  className="text-sm text-[#a1a1aa] leading-relaxed pb-4"
                >
                  {item.body()}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#121212] border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6">
            Ready to Play?
          </h2>
          <p className="text-base text-[#a1a1aa] mb-8">
            Secure your court in minutes. Name, email, and you're on the court.
          </p>
          <button
            data-testid="cta-book-button"
            onClick={() => navigate("/courts")}
            className="btn-primary"
          >
            Browse Courts
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center text-sm text-[#a1a1aa]">
          © 2026 Assenda Pickleball. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
